import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSendMobileCode, useConfirmMobileCode, storeAuthToken } from "../hooks/useAuth";
import { hideMobilePopup, setLoader, setCustomerData, setAuthorizationToken } from "../actions";
import history from "../history";

/**
 * Mobile Code Popup - New Amplify Gen 2 Version
 *
 * Uses GraphQL mutations for SMS-based authentication
 */
const MobileCodePopupNew = () => {
  const dispatch = useDispatch();
  const expired_link = useSelector((state) => state.expired_link);
  const jwt = useSelector((state) => state.jwt);

  // Form state
  const [step, setStep] = useState(expired_link?.status ? 'confirm' : 'send');
  const [mobile, setMobile] = useState(expired_link?.mobile || '');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  // Mutations
  const sendCodeMutation = useSendMobileCode();
  const confirmCodeMutation = useConfirmMobileCode();

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    const mobileRegex = /^\+?\d{10,15}$/;
    const cleanMobile = mobile.replace(/\s/g, '');

    if (!cleanMobile || !mobileRegex.test(cleanMobile)) {
      setError('Please enter a valid mobile number');
      return;
    }

    dispatch(setLoader(true, "Sending Code..."));

    try {
      const result = await sendCodeMutation.mutateAsync(cleanMobile);

      dispatch(setLoader(false, null));

      if (result.success) {
        setMobile(result.mobile || cleanMobile);
        setStep('confirm');
      } else {
        setError(result.message || 'Failed to send code');
      }
    } catch (err) {
      dispatch(setLoader(false, null));
      setError('Failed to send verification code. Please try again.');
      console.error('Send code error:', err);
    }
  };

  const handleConfirmCode = async (e) => {
    e.preventDefault();
    setError('');

    if (!code || code.trim().length === 0) {
      setError('Please enter the verification code');
      return;
    }

    dispatch(setLoader(true, "Confirming Code..."));

    try {
      const result = await confirmCodeMutation.mutateAsync({
        mobile,
        code: code.trim(),
      });

      dispatch(setLoader(false, null));

      if (result.success) {
        // Store the JWT token
        if (result.token) {
          storeAuthToken(result.token);
          localStorage.token = result.token;
          localStorage.mobile = mobile;
        }

        if (result.isNewUser) {
          // New user - set customer data for registration
          dispatch(setCustomerData({
            mobile: mobile,
            fullname: result.user?.first_name && result.user?.last_name
              ? `${result.user.first_name} ${result.user.last_name}`
              : '',
            email: result.user?.email || '',
          }));
        } else {
          // Existing user - set full auth state
          localStorage.email = result.user?.email || '';
          localStorage.first_name = result.user?.first_name || '';
          localStorage.last_name = result.user?.last_name || '';

          dispatch(setAuthorizationToken({
            token: result.token,
            customer: {
              mobile: mobile,
              first_name: result.user?.first_name || '',
              last_name: result.user?.last_name || '',
              email: result.user?.email || '',
              opt_in_flag: result.user?.opt_in_flag || 0,
            },
          }));
        }

        setStep('success');

        // Close popup and redirect
        setTimeout(() => {
          dispatch(hideMobilePopup());

          setTimeout(() => {
            if (expired_link?.status) {
              localStorage.autologin = false;
              history.push(`/autologin/${result.token}`);
            } else if (result.isNewUser) {
              // Redirect new users to registration
              history.push('/register');
            }
            // Existing users stay on current page - already logged in
          }, 100);
        }, 1500);
      } else {
        setError(result.message || 'Invalid verification code');
      }
    } catch (err) {
      dispatch(setLoader(false, null));
      setError('Failed to verify code. Please try again.');
      console.error('Confirm code error:', err);
    }
  };

  const renderSendCodeForm = () => (
    <form onSubmit={handleSendCode} className="form">
      <div className="mobile-popup-heading">Send Mobile Code</div>
      <div className="input-box">
        <input
          type="text"
          placeholder="Your mobile (e.g., 0412345678)"
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          autoFocus
        />
        {error && <div className="input-validation">{error}</div>}
      </div>
      <button type="submit" disabled={sendCodeMutation.isLoading}>
        {sendCodeMutation.isLoading ? 'Sending...' : 'Send Code'}
      </button>
    </form>
  );

  const renderConfirmCodeForm = () => (
    <form onSubmit={handleConfirmCode} className="form">
      <div className="mobile-popup-heading">Confirm Mobile Code</div>
      <div className="mobile-popup-subtext">
        Enter the 6-digit code sent to {mobile}
      </div>
      <div className="input-box">
        <input
          type="text"
          placeholder="Enter 6-digit code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          maxLength={6}
          autoFocus
        />
        {error && <div className="input-validation">{error}</div>}
      </div>
      <button type="submit" disabled={confirmCodeMutation.isLoading}>
        {confirmCodeMutation.isLoading ? 'Confirming...' : 'Confirm Code'}
      </button>
      <div
        className="mobile-popup-resend"
        onClick={() => {
          setStep('send');
          setCode('');
          setError('');
        }}
      >
        Didn't receive a code? Try again
      </div>
    </form>
  );

  const renderSuccess = () => (
    <div>
      <div className="mobile-popup-heading">Success</div>
      <div className="mobile-popup-subtext">Mobile code confirmed</div>
    </div>
  );

  return (
    <div id="mobile-popup-mask">
      <div id="mobile-popup">
        <div
          id="mobile-popup-x"
          onClick={() => dispatch(hideMobilePopup())}
        >
          x
        </div>
        {step === 'send' && renderSendCodeForm()}
        {step === 'confirm' && renderConfirmCodeForm()}
        {step === 'success' && renderSuccess()}
      </div>
    </div>
  );
};

export default MobileCodePopupNew;
